import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

function LandingPage() {
  const navigate = useNavigate();
  const [showIntro, setShowIntro] = useState(true);
  const [introFading, setIntroFading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fadeTimer = setTimeout(() => setIntroFading(true), 1800);
    const hideTimer = setTimeout(() => setShowIntro(false), 2400);
    return () => { clearTimeout(fadeTimer); clearTimeout(hideTimer); };
  }, []);

  const features = [
    { icon: '👥', title: 'Real-time Collaboration', description: 'Share projects and edit simultaneously with supervisors or co-authors — completely free!' },
    { icon: '📝', title: '30+ Professional Templates', description: 'IEEE, Springer, ACM, IIT/NIT thesis, assignments and more' },
    { icon: '⚡', title: 'Real-time PDF Compilation', description: 'Instant PDF preview with powerful pdfLaTeX engine' },
    { icon: '📁', title: 'Multi-file Projects', description: 'Organize thesis chapters, assignments with folders' },
    { icon: '💾', title: 'Auto-save & Secure', description: 'Your research papers are automatically saved and private' },
    { icon: '🔧', title: 'Formatting Toolbar', description: 'Easy LaTeX formatting with one-click bold, italic, sections, tables and more' },
    { icon: '🔒', title: 'Privacy & Security', description: 'Your documents are encrypted and accessible only to you' }
  ];

  const templates = [
    'IEEE Conference', 'Springer Journal', 'ACM Paper',
    'PhD Thesis', 'B.Tech Project', 'Resume/CV',
    'Beamer Presentation', 'Assignment', 'And 20+ more...'
  ];

  const shortcuts = [
    ['Ctrl + Enter', 'Compile PDF'],
    ['Ctrl + S', 'Save Project'],
    ['Ctrl + D', 'Download PDF'],
    ['Ctrl + Z / Ctrl+Shift+Z', 'Undo / Redo'],
    ['Ctrl + /', 'Comment line'],
    ['Ctrl + F', 'Find in editor'],
    ['Ctrl + H', 'Find & Replace'],
    ['Tab', 'Accept autocomplete'],
    ['\\begin{env} + Enter', 'Auto-closes with \\end{env}'],
  ];

  return (
    <>
      {/* Logo Intro */}
      {showIntro && (
        <div className={`logo-intro ${introFading ? 'logo-intro-fade' : ''}`}>
          <div className="logo-intro-inner">
            <img src="/logo.png" alt="LaTeXLaB" className="logo-intro-img" onError={(e) => e.target.style.display='none'} />
            <div className="logo-intro-text">LaTeX<span>LaB</span></div>
          </div>
        </div>
      )}

      <div className="landing-page">

        {/* Navbar */}
        <nav className="navbar">
          <div className="navbar-inner">
            <span className="navbar-brand" onClick={() => window.scrollTo({top:0,behavior:'smooth'})}>
              LaTeX<span>LaB</span>

            </span>
            <div className="navbar-links">
              <button onClick={() => scrollToSection('features')}>Features</button>
              <button onClick={() => scrollToSection('templates')}>Templates</button>
              <button onClick={() => scrollToSection('howto')}>How to Use</button>
              <button onClick={() => scrollToSection('shortcuts')}>Shortcuts</button>
              <button className="nav-btn-login" onClick={() => navigate('/login')}>Login</button>
              <button className="nav-btn-login" onClick={() => navigate('/editor')}>Try Free</button>
              <button className="nav-btn-primary" onClick={() => navigate('/register')}>Sign Up</button>
            </div>
          </div>
        </nav>


        {/* Hero */}
        <section className="hero">
          <div className="container">
            <div className="hero-content">
              <div className="hero-brand">LaTeXLaB</div>
              <h1 className="hero-title">
                Write Research Papers in LaTeX<br />
                <span className="highlight">Free, Online & No Installation</span>
              </h1>
              <p className="hero-subtitle">
                Trusted by students and researchers across India.<br />
                30+ templates for IEEE, Springer, ACM, IIT Thesis and more.
              </p>
              <div className="hero-buttons">
                <button className="btn btn-primary btn-large" onClick={() => navigate('/editor')}>🚀 Try Editor Free</button>
                <button className="btn btn-secondary btn-large" onClick={() => navigate('/register')}>Sign Up Free</button>
              </div>
              <p className="hero-note">✨ No credit card • 10 free compilations/day • 30+ templates</p>
              <div style={{
                marginTop:'30px', background:'rgba(255,255,255,0.15)',
                borderRadius:'12px', padding:'12px',
                border:'1px solid rgba(255,255,255,0.3)'
              }}>
                <img 
                  src="/editor-preview.png" 
                  alt="LaTeXLaB Editor Preview"
                  onError={(e) => e.target.style.display='none'}
                  style={{width:'100%', borderRadius:'8px', maxHeight:'300px', objectFit:'cover'}}
                />
                <div style={{color:'white', fontSize:'13px', marginTop:'8px', opacity:'0.9'}}>
                  ⬆️ Real-time PDF preview • Write LaTeX • Compile instantly
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="features" id="features">
          <div className="container">
            <h2 className="section-title">Everything You Need for LaTeX</h2>
            <div className="features-grid">
              {features.map((feature, index) => (
                <div key={index} className="feature-card">
                  <div className="feature-icon">{feature.icon}</div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Templates */}
        <section className="templates" id="templates">
          <div className="container">
            <h2 className="section-title">28+ Professional Templates</h2>
            <p className="section-subtitle">Start with ready-made templates for every academic need</p>
            <div className="templates-list">
              {templates.map((template, index) => (
                <span key={index} className="template-tag">{template}</span>
              ))}
            </div>
            <button className="btn btn-primary" onClick={() => navigate('/register')}>Explore All Templates</button>
          </div>
        </section>

        {/* Why */}
        <section className="why-section" id="why">
          <div className="container">
            <h2 className="section-title">Why LaTeXLaB?</h2>
            <div className="comparison-grid">
              <div className="comparison-card"><h3>🚀 Fast & Simple</h3><p>No installation needed. Start writing in seconds with our online editor.</p></div>
              <div className="comparison-card"><h3>🎯 Purpose-Built</h3><p>Designed specifically for thesis writing, research papers, and academic work.</p></div>
              <div className="comparison-card"><h3>🔒 Secure</h3><p>Your research papers and documents are safe and private.</p></div>
            </div>
          </div>
        </section>

        {/* How to Use */}
        <section className="howto-section" id="howto">
          <div className="container">
            <h2 className="section-title">How to Use LaTeXLaB</h2>
            <p className="section-subtitle">Get started in 4 simple steps</p>
            <div className="howto-grid">
              {[
                { step: '1', icon: '📝', title: 'Pick a Template', desc: 'Choose from 28+ templates — IEEE, thesis, assignment, blank document and more.' },
                { step: '2', icon: '✍️', title: 'Write LaTeX', desc: 'Use the editor with autocomplete. Type \\begin{itemize} and it auto-closes for you.' },
                { step: '3', icon: '⚡', title: 'Compile PDF', desc: 'Click Compile PDF or press Ctrl+Enter. See your PDF instantly on the right.' },
                { step: '4', icon: '⬇️', title: 'Download', desc: 'Download your PDF, .tex source file, or full project as a ZIP.' },
              ].map((item) => (
                <div key={item.step} className="howto-card">
                  <div className="howto-step">{item.step}</div>
                  <div className="howto-icon">{item.icon}</div>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: '30px' }}>
              <a href="/HowToUse.html" target="_blank" className="btn btn-secondary">📖 Read Full Guide</a>
            </div>
          </div>
        </section>

        {/* Shortcuts */}
        <section className="shortcuts-section" id="shortcuts">
          <div className="container">
            <h2 className="section-title">⌨️ Keyboard Shortcuts</h2>
            <p className="section-subtitle">Work faster with these shortcuts</p>
            <div className="shortcuts-table-wrap">
              <table className="shortcuts-table">
                <thead><tr><th>Shortcut</th><th>Action</th></tr></thead>
                <tbody>
                  {shortcuts.map(([key, desc]) => (
                    <tr key={key}>
                      <td><code>{key}</code></td>
                      <td>{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="cta-section">
          <div className="container">
            <h2>Ready to Start Writing?</h2>
            <p>Join researchers and students using LaTeXLaB</p>
            <button className="btn btn-primary btn-large" onClick={() => navigate('/register')}>Start Your Free Trial</button>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-content">
              <div className="footer-section">
                <img src="/logo.png" alt="LaTeXLaB" className="footer-logo" onError={(e) => e.target.style.display='none'} />
                <p>Professional LaTeX editing made simple</p>
              </div>
              <div className="footer-section">
                <h4>Product</h4>
                <ul>
                  <li><button className="footer-link" onClick={() => scrollToSection('features')}>Features</button></li>
                  <li><button className="footer-link" onClick={() => scrollToSection('templates')}>Templates</button></li>
                  <li><button className="footer-link" onClick={() => navigate('/editor')}>Try Editor</button></li>
                </ul>
              </div>
              <div className="footer-section">
                <h4>Help</h4>
                <ul>
                  <li><button className="footer-link" onClick={() => scrollToSection('howto')}>How to Use</button></li>
                  <li><button className="footer-link" onClick={() => scrollToSection('shortcuts')}>Shortcuts</button></li>
                  <li><a href="/HowToUse.html" target="_blank" style={{color:'#9ca3af', textDecoration:'none'}}>Full Guide</a></li>
                </ul>
              </div>
              <div className="footer-section">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About</a></li>
                  <li><a href="/privacy">Privacy Policy</a></li>
                  <li><a href="/terms">Terms of Service</a></li>
                </ul>
              </div>
              <div className="footer-section">
                <h4>Contact</h4>
                <ul>
                  <li>support@latexlab.in</li>
                  <li>Made with 💚 in India</li>
                </ul>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2025 LaTeXLaB. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export default LandingPage;
